import { useState, useEffect } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChartLineUp, Users, Package, Storefront, Trophy, Warning, X, MagnifyingGlassPlus, MagnifyingGlassMinus, CaretDown, CaretUp, ArrowRight, FilePdf, Funnel, Stack, MagnifyingGlass } from "@phosphor-icons/react";
import OptimizedImage from "@/components/OptimizedImage";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Circular progress component
const CircularProgress = ({ value, max, color = "#009392", size = 120, label, sublabel }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size/2}
            cy={size/2}
            r="45"
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx={size/2}
            cy={size/2}
            r="45"
            stroke={color}
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-stone-800">{value}</span>
        </div>
      </div>
      <p className="text-sm font-medium text-stone-700 mt-2">{label}</p>
      {sublabel && <p className="text-xs text-stone-400">{sublabel}</p>}
    </div>
  );
};

// Large stat card with icon
const BigStatCard = ({ icon: Icon, label, value, sublabel, color = "text-[#009392]", bgColor = "bg-gradient-to-br from-teal-50 to-white" }) => (
  <div className={`${bgColor} rounded-2xl p-6 border border-stone-100 shadow-sm hover:shadow-md transition-shadow`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">{label}</p>
        <p className={`text-4xl font-bold mt-2 ${color}`}>{value}</p>
        {sublabel && <p className="text-sm text-stone-400 mt-1">{sublabel}</p>}
      </div>
      <div className={`p-3 rounded-xl ${bgColor === "bg-gradient-to-br from-teal-50 to-white" ? "bg-teal-100" : "bg-white/50"}`}>
        <Icon size={28} weight="duotone" className={color} />
      </div>
    </div>
  </div>
);

// Horizontal bar with animation
const AnimatedBar = ({ label, value, maxValue, color = "#009392" }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  
  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-stone-600">{label}</span>
        <span className="text-sm font-bold text-stone-800">{value}</span>
      </div>
      <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-1000 ease-out group-hover:opacity-80"
          style={{ 
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${color}, ${color}cc)`
          }}
        />
      </div>
    </div>
  );
};

export default function Statistics() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showAllClients, setShowAllClients] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateFilter, setDateFilter] = useState("all"); // all, month, quarter, year
  const [seriesExposicao, setSeriesExposicao] = useState([]);
  const [totalClientsForSeries, setTotalClientsForSeries] = useState(0);
  const [seriesFilter, setSeriesFilter] = useState("");
  const [expandedSeries, setExpandedSeries] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, clientsRes, seriesRes] = await Promise.all([
          axios.get(`${API}/statistics`),
          axios.get(`${API}/clients`),
          axios.get(`${API}/series-exposicao`)
        ]);
        setStats(statsRes.data);
        setClients(clientsRes.data || []);
        setSeriesExposicao(seriesRes.data?.series || []);
        setTotalClientsForSeries(seriesRes.data?.total_clients || 0);
      } catch (error) {
        console.error("Error fetching statistics:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Export to PDF function - uses backend WeasyPrint
  const exportToPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/statistics/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `estatisticas-gresart-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting PDF:", error);
    } finally {
      setExporting(false);
    }
  };

  // Filter clients by date
  const getFilteredClients = () => {
    if (dateFilter === "all") return clients;
    
    const now = new Date();
    let startDate;
    
    switch (dateFilter) {
      case "month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "quarter":
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case "year":
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return clients;
    }
    
    return clients.filter(c => {
      const createdAt = new Date(c.created_at);
      return createdAt >= startDate;
    });
  };

  const filteredClients = getFilteredClients();

  // Prepare chart data for size distribution
  const getSizeChartData = () => {
    if (!stats?.panels?.size_distribution) return [];
    return Object.entries(stats.panels.size_distribution)
      .map(([size, count]) => ({
        name: size || "Sem tamanho",
        value: count
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  };

  // Prepare chart data for collections
  const getCollectionChartData = () => {
    if (!stats?.panels?.collection_distribution) return [];
    return Object.entries(stats.panels.collection_distribution)
      .map(([collection, count]) => ({
        name: collection || "Sem coleção",
        value: count
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  };

  const COLORS = ['#009392', '#00b3b0', '#39c0c0', '#72d0d0', '#a8e0e0', '#d4f0f0'];
  const COLORS_ALT = ['#009392', '#3366cc', '#dc3912', '#ff9900', '#109618', '#990099'];

  const openImageModal = (imageUrl, code) => {
    setSelectedImage({ url: imageUrl, code });
    setZoomLevel(1);  // Start at 100%
    setPanPosition({ x: 0, y: 0 });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => { setZoomLevel(prev => Math.max(prev - 0.25, 0.5)); setPanPosition({ x: 0, y: 0 }); };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size={48} />
        </div>
      </div>
    );
  }

  const totalPanels = (stats?.panels?.total || 0) + (stats?.panels?.custom_panels || 0);
  const maxSizeValue = Math.max(...Object.values(stats?.panels?.size_distribution || {}), 1);

  const filteredSeriesExposicao = seriesFilter.trim()
    ? seriesExposicao.filter((s) => s.series.toLowerCase().includes(seriesFilter.trim().toLowerCase()))
    : [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <PageHeader
          icon={ChartLineUp}
          title="Estatísticas"
          subtitle="Visão geral do sistema"
          actions={
            <div className="flex items-center gap-3 hide-in-pdf">
              {/* Date Filter */}
              <div className="flex items-center gap-2 bg-white rounded-lg border border-stone-200 p-1">
                <Funnel size={16} className="text-stone-400 ml-2" />
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="text-sm bg-transparent border-none outline-none pr-2 py-1 text-stone-600"
                  data-testid="date-filter"
                >
                  <option value="all">Todo o período</option>
                  <option value="month">Este mês</option>
                  <option value="quarter">Este trimestre</option>
                  <option value="year">Este ano</option>
                </select>
              </div>

              {/* Export PDF Button */}
              <Button
                onClick={exportToPDF}
                disabled={exporting}
                className="bg-[#009392] hover:bg-[#007a7a] text-white flex items-center gap-2"
                data-testid="export-pdf-btn"
              >
                <FilePdf size={18} />
                {exporting ? "Exportando..." : "Exportar PDF"}
              </Button>
            </div>
          }
        />
      </div>

      {/* Main Stats Grid - 3 big cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" style={{ pageBreakInside: 'avoid' }}>
        <BigStatCard 
          icon={Users}
          label="Clientes"
          value={stats?.clients?.total || 0}
          sublabel="clientes registados"
          bgColor="bg-gradient-to-br from-blue-50 to-white"
          color="text-blue-600"
        />
        <BigStatCard 
          icon={Package}
          label="Painéis em Showrooms"
          value={totalPanels}
          sublabel={`${stats?.panels?.total || 0} catálogo + ${stats?.panels?.custom_panels || 0} descontinuados`}
        />
        <BigStatCard 
          icon={ChartLineUp}
          label="Investimento Total"
          value={`€${(stats?.investment?.total || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}`}
          sublabel={stats?.clients?.total > 1 ? `média €${(stats?.investment?.average || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2 })} por cliente` : null}
          bgColor="bg-gradient-to-br from-emerald-50 to-white"
          color="text-emerald-600"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" style={{ pageBreakInside: 'avoid' }}>
        {/* Size Distribution Bar Chart */}
        <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" style={{ pageBreakInside: 'avoid' }}>
          <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <ChartLineUp size={20} className="text-[#009392]" />
            Distribuição por Tamanho
          </h3>
          {getSizeChartData().length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={getSizeChartData()} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 11, fill: '#78716c' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#78716c' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                  formatter={(value) => [`${value} painéis`, 'Quantidade']}
                />
                <Bar 
                  dataKey="value" 
                  fill="#009392" 
                  radius={[4, 4, 0, 0]}
                  animationDuration={1000}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-stone-400">
              Sem dados disponíveis
            </div>
          )}
        </div>

        {/* Collection Distribution Pie Chart */}
        <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" style={{ pageBreakInside: 'avoid' }}>
          <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <Package size={20} className="text-blue-600" />
            Distribuição por Coleção
          </h3>
          {getCollectionChartData().length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={getCollectionChartData()}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  animationDuration={1000}
                >
                  {getCollectionChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS_ALT[index % COLORS_ALT.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                  formatter={(value, name) => [`${value} painéis`, name]}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value) => <span className="text-xs text-stone-600">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-stone-400">
              Sem dados disponíveis
            </div>
          )}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left: Panel Status & Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" style={{ pageBreakInside: 'avoid' }}>
            <h3 className="text-lg font-semibold text-stone-800 mb-6 flex items-center gap-2">
            <Package size={20} className="text-[#009392]" />
            Estado dos Painéis
          </h3>
          
          {/* Circular indicators */}
          <div className="flex justify-around mb-6">
            <CircularProgress 
              value={stats?.panels?.with_defects || 0} 
              max={totalPanels}
              color="#f59e0b"
              label="Com Defeito"
              sublabel={`${stats?.panels?.defect_percentage || 0}%`}
            />
            <CircularProgress 
              value={stats?.panels?.custom_panels || 0} 
              max={totalPanels}
              color="#8b5cf6"
              label="Descontinuados"
              sublabel={stats?.panels?.custom_panels_value > 0 ? `€${stats.panels.custom_panels_value.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}` : null}
            />
          </div>
          
          {/* Size bars */}
          <div className="space-y-4 mt-6 pt-6 border-t border-stone-100">
            {Object.entries(stats?.panels?.size_distribution || {}).map(([size, count]) => (
              <AnimatedBar 
                key={size}
                label={size}
                value={count}
                maxValue={maxSizeValue}
                color={count > 0 ? "#009392" : "#d1d5db"}
              />
            ))}
          </div>
        </div>

        {/* Right: Top Clients & Catalogs */}
        <div className="space-y-6">
          {/* Top Clients */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" style={{ pageBreakInside: 'avoid' }}>
            <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Trophy size={20} className="text-yellow-500" />
              Top Clientes por Investimento
            </h3>
            <div className="space-y-3">
              {(stats?.clients?.top_by_investment || [])
                .slice(0, showAllClients ? undefined : 5)
                .map((client, index) => (
                <div key={client.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 transition-colors">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white' :
                    index === 1 ? 'bg-gradient-to-br from-stone-300 to-stone-400 text-white' :
                    index === 2 ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white' :
                    'bg-stone-100 text-stone-500'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{client.name}</p>
                    <p className="text-xs text-stone-400">{client.city}</p>
                  </div>
                  <span className="text-sm font-bold text-[#009392]">
                    €{client.investment.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
              {(stats?.clients?.top_by_investment || []).length === 0 && (
                <p className="text-sm text-stone-400 text-center py-4">Sem dados disponíveis</p>
              )}
              {(stats?.clients?.top_by_investment || []).length > 5 && (
                <Button 
                  variant="ghost" 
                  className="w-full mt-2 text-[#009392] hover:text-[#007a7a] hover:bg-[#009392]/5"
                  onClick={() => setShowAllClients(!showAllClients)}
                >
                  {showAllClients ? (
                    <>
                      <CaretUp size={16} className="mr-2" />
                      Ver menos
                    </>
                  ) : (
                    <>
                      <CaretDown size={16} className="mr-2" />
                      Ver todos ({(stats?.clients?.top_by_investment || []).length} clientes)
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Catalogs Delivered */}
          {(stats?.catalogs?.total_delivered || 0) > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Storefront size={20} className="text-cyan-600" />
                Catálogos Entregues
              </h3>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-cyan-600">{stats?.catalogs?.total_delivered}</p>
                  <p className="text-xs text-stone-400">total</p>
                </div>
                <div className="flex-1 space-y-2">
                  {Object.entries(stats?.catalogs?.by_type || {}).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between text-sm">
                      <span className="text-stone-600 truncate">{name}</span>
                      <span className="font-semibold text-stone-800 ml-2">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top 10 Products - Full Width */}
      {(stats?.panels?.top_used || []).length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm mb-8" style={{ pageBreakInside: 'avoid' }}>
          <h3 className="text-lg font-semibold text-stone-800 mb-6 flex items-center gap-2">
            <Trophy size={20} className="text-[#009392]" />
            Top 10 Painéis Mais Usados
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-4">
            {(stats?.panels?.top_used || []).map((product, index) => (
              <div 
                key={product.id} 
                className="group relative cursor-pointer"
                onClick={() => openImageModal(product.image_url, product.code)}
              >
                <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold z-10 ${
                  index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white' :
                  index === 1 ? 'bg-gradient-to-br from-stone-300 to-stone-400 text-white' :
                  index === 2 ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white' :
                  'bg-stone-200 text-stone-600'
                }`}>
                  {index + 1}
                </div>
                <div className="bg-stone-50 rounded-xl p-2 hover:shadow-md transition-shadow hover:ring-2 hover:ring-[#009392]">
                  <OptimizedImage 
                    src={product.image_url} 
                    alt={product.code}
                    className="w-full h-auto object-contain rounded-lg"
                    style={{ aspectRatio: '1920/1005', maxHeight: '120px' }}
                    width={200}
                  />
                  <div className="mt-2 text-center">
                    <p className="text-xs font-medium text-stone-700 truncate">{product.code}</p>
                    <p className="text-lg font-bold text-[#009392]">{product.usage_count}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Análise de Séries em Exposição */}
      <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm mb-8" data-testid="series-exposicao-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Stack size={20} className="text-[#009392]" />
            Análise de Séries em Exposição
          </h3>
          <div className="relative w-full sm:w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              placeholder="Filtrar por série..."
              value={seriesFilter}
              onChange={(e) => setSeriesFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#009392]/40"
              data-testid="input-filtro-serie-exposicao"
            />
          </div>
        </div>

        {filteredSeriesExposicao.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-8" data-testid="series-exposicao-empty">
            {seriesFilter.trim() ? "Nenhuma série encontrada com esse filtro" : "Pesquise uma série para ver a análise de exposição"}
          </p>
        ) : (
          <div className="space-y-2" data-testid="series-exposicao-list">
            {filteredSeriesExposicao.map((s) => {
              const isExpanded = expandedSeries === s.series;
              return (
                <div key={s.series} className="border border-stone-100 rounded-xl overflow-hidden" data-testid={`serie-exposicao-item-${s.series.toLowerCase().replace(/\s+/g, '-')}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedSeries(isExpanded ? null : s.series)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-stone-50 transition-colors"
                    data-testid={`btn-toggle-serie-${s.series.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-semibold text-stone-800 truncate">{s.series}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#009392]/10 text-[#009392] font-medium flex-shrink-0">
                        {s.client_count} de {totalClientsForSeries} clientes
                      </span>
                    </div>
                    {isExpanded ? <CaretUp size={18} className="text-stone-400 flex-shrink-0" /> : <CaretDown size={18} className="text-stone-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 pb-4 pt-1 border-t border-stone-100" data-testid={`serie-detalhe-${s.series.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div>
                        <p className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-2">Com a Série ({s.clients_with.length})</p>
                        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                          {s.clients_with.length === 0 ? (
                            <p className="text-xs text-stone-400">Nenhum cliente</p>
                          ) : s.clients_with.map((c) => (
                            <Link
                              key={c.id}
                              to={`/clients/${c.id}`}
                              className="flex items-center justify-between gap-2 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-1.5 transition-colors"
                              data-testid={`serie-cliente-com-${c.id}`}
                            >
                              <span className="text-sm text-stone-800 truncate">{c.name}</span>
                              <span className="text-xs text-stone-500 flex-shrink-0">{c.city || "-"}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">Sem a Série - Potencial ({s.clients_without.length})</p>
                        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                          {s.clients_without.length === 0 ? (
                            <p className="text-xs text-stone-400">Nenhum cliente</p>
                          ) : s.clients_without.map((c) => (
                            <Link
                              key={c.id}
                              to={`/clients/${c.id}`}
                              className="flex items-center justify-between gap-2 bg-amber-50 hover:bg-amber-100 rounded-lg px-3 py-1.5 transition-colors"
                              data-testid={`serie-cliente-sem-${c.id}`}
                            >
                              <span className="text-sm text-stone-800 truncate">{c.name}</span>
                              <span className="text-xs text-stone-500 flex-shrink-0">{c.city || "-"}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* All Clients with Stats */}
      {filteredClients.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-testid="all-clients-stats" style={{ pageBreakInside: 'avoid' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
              <Storefront size={20} className="text-[#009392]" />
              Todos os Clientes
              {dateFilter !== "all" && (
                <span className="text-xs font-normal text-stone-400 ml-2">
                  ({filteredClients.length} de {clients.length})
                </span>
              )}
            </h3>
            <Link to="/clients" className="text-sm text-[#009392] hover:underline flex items-center gap-1">
              Ver lista completa <ArrowRight size={14} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredClients.map((client) => (
              <Link 
                key={client.id} 
                to={`/clients/${client.id}`}
                className="group bg-stone-50 rounded-xl p-4 border border-stone-100 hover:shadow-md hover:border-[#009392]/30 hover:bg-white transition-all duration-200"
                data-testid={`client-stat-card-${client.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-stone-800 truncate group-hover:text-[#009392] transition-colors">
                      {client.name}
                    </h4>
                    <p className="text-xs text-stone-500 truncate">{client.city || "Sem cidade"}</p>
                  </div>
                  <ArrowRight size={16} className="text-stone-300 group-hover:text-[#009392] transition-colors flex-shrink-0 mt-1" />
                </div>
                
                {/* Stats - always visible, more prominent on hover */}
                <div className="mt-3 pt-3 border-t border-stone-200 grid grid-cols-3 gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#009392]">{client.product_count || 0}</div>
                    <div className="text-[10px] text-stone-500">Painéis</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">{client.expositor_count || 0}</div>
                    <div className="text-[10px] text-stone-500">Expositores</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{formatCurrency(client.total_value || 0).replace('€', '').trim()}</div>
                    <div className="text-[10px] text-stone-500">Valor €</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Image Modal with Zoom and Pan */}
      <Dialog open={!!selectedImage} onOpenChange={closeImageModal}>
        <DialogContent 
          className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-4xl p-0 bg-black/95 border-none"
          onWheel={(e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setZoomLevel(prev => Math.min(Math.max(prev + delta, 0.5), 3));
          }}
        >
          <div className="relative">
            {/* Close button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 z-20 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X size={24} className="text-white" />
            </button>
            
            {/* Hint */}
            <div className="absolute top-4 left-4 text-white/50 text-xs z-20">
              Scroll para zoom • Arrastra para mover
            </div>
            
            {/* Zoom controls */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex items-center gap-2 bg-white/10 rounded-full p-1">
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                disabled={zoomLevel <= 0.5}
              >
                <MagnifyingGlassMinus size={20} className="text-white" />
              </button>
              <span className="text-white text-sm px-2 min-w-[50px] text-center">{Math.round(zoomLevel * 100)}%</span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                disabled={zoomLevel >= 3}
              >
                <MagnifyingGlassPlus size={20} className="text-white" />
              </button>
              {/* Reset button */}
              <button
                onClick={() => { setZoomLevel(1); setPanPosition({ x: 0, y: 0 }); }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors ml-2 text-white text-xs"
                title="Reset"
              >
                100%
              </button>
            </div>
            
            {/* Image container with drag */}
            <div 
              className="overflow-auto max-h-[85vh] flex items-center justify-center p-4 cursor-grab active:cursor-grabbing"
              onMouseDown={(e) => {
                if (zoomLevel > 1) {
                  e.preventDefault();
                  setIsDragging(true);
                  setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
                }
              }}
              onMouseMove={(e) => {
                if (isDragging && zoomLevel > 1) {
                  setPanPosition({
                    x: e.clientX - dragStart.x,
                    y: e.clientY - dragStart.y
                  });
                }
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
            >
              <img
                src={selectedImage?.url}
                alt={selectedImage?.code}
                className="select-none object-contain"
                style={{ 
                  maxWidth: zoomLevel === 1 ? '100%' : 'none',
                  maxHeight: zoomLevel === 1 ? '80vh' : 'none',
                  width: zoomLevel > 1 ? `${zoomLevel * 100}%` : 'auto',
                  transform: zoomLevel > 1 ? `translate(${panPosition.x}px, ${panPosition.y}px)` : 'none',
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                }}
                draggable={false}
              />
            </div>
            
            {/* Image code */}
            {selectedImage?.code && (
              <div className="absolute top-12 left-4 bg-white/10 px-3 py-1 rounded-full">
                <span className="text-white text-sm font-medium">{selectedImage.code}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
